// Service
import { ReferralCommissionHistoryDataService } from "./services/referral-commission-history-data.service";
import { ReferralCommissionSettingsDataService } from '../referral-commission-settings/services/referral-commission-settings-data.service';
// Angular / Core
import { FormGroup, FormControl } from "@angular/forms";
import { Component, OnInit } from "@angular/core";
import { LoadingBarService } from "@ngx-loading-bar/core";
// Third Party
import { TransactionHttpService } from '@core/services/transaction-http.service';
import * as moment from 'moment-timezone';
import { Pagination } from '@core/models/pagination.model';
import { Observable, of, Subscription } from 'rxjs';
import { tap, map, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { ReferralCommissionHistory } from '@core/models/referral-commission-history.model';
import { ReferralCommissionSetting } from '@core/models/referral-commission-setting.model';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { AppPermissionService } from '@core/services/app-permission.service';

@Component({
  selector: "kt-referral-commission-history",
  templateUrl: "./referral-commission-history.component.html",
  styleUrls: ["./referral-commission-history.component.scss"],
})

export class ReferralCommissionHistoryComponent implements OnInit {
  form: FormGroup;
  referralCommissionHistory$: Observable<ReferralCommissionHistory[]>;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  referralCommissionSettings = [];
  dataLength = 0;
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  ranges: any;
  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  currenciesDropdownList = JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies'));
  dropdown = {
    statuses: this.dropdownHttpService.referralCommHistoryStatuses,
    perPage: this.dropdownHttpService.perPage,
    types : this.dropdownHttpService.referralCommSettingTypes,
  };

  sortingConfig = {
    'id': 'desc',
    'currency_code': 'desc',
    'start_datetime': 'desc',
    'commission_name': 'desc',
    'total_amount': 'desc',
    'status': 'desc',
    'remarks': 'desc',
    'created_at': 'desc',
    'updated_at': 'desc',
  };
  sortingSelection = {
    'sort_by': 'id',
    'sort_order': 'desc',
  };

  sortingStorageName = 'sortingConfig';
  sortingStorageGroup = '17.2';

  // permissions
  canViewReferralCommissionHistory: boolean;

  private subscription = new Subscription();
  private subscriptions = new Subscription();
  
  constructor(
    private loadingBar: LoadingBarService,
    private referralCommissionHistoryDataService: ReferralCommissionHistoryDataService,
    private referralCommissionSettingsDataService: ReferralCommissionSettingsDataService,
    private transactionHttpService: TransactionHttpService,
    private dropdownHttpService: DropdownHttpService,
    private appPermissionService: AppPermissionService,
  ) { }

  ngOnInit() {
    localStorage.setItem('sortingConfig', JSON.stringify({ [this.sortingStorageGroup]: this.sortingConfig }));
    this.referralCommissionSettingsDataService.getWithQuery(`?paginate=false`).subscribe((res: any) => {
      this.referralCommissionSettings = res;
    });
    this.pagination = this.referralCommissionHistoryDataService.pagination;
    this.ranges = this.transactionHttpService.ranges;
    this.formInit();
    this.onSubmit(true);

    const apSub = this.appPermissionService.getAppPermissions().subscribe((appPermissions) => {
      this.canViewReferralCommissionHistory = appPermissions.view_referral_commission_history;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = clearSearch ? false : true;
    this.loading = true;
    if (this.form.value.name) {
      this.form.value.name = this.form.value.name.name;
    }
    of(this.form.value).pipe(
      map(this.filterFormFields),
      tap((data) => {
        if (data['start_datetime']) {
          data['start_datetime'] = moment(data['start_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_datetime'] = moment(data['end_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }
        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.loadingBar.start();
        this.referralCommissionHistory$ = this.referralCommissionHistoryDataService.getWithQuery(`?${this.params}&perPage=${this.pageSize}`).pipe(
          tap(res => {
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.dataLength = res.length;
            this.page = 1;
            this.pagination = this.referralCommissionHistoryDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();

  }

  onClear() {
    this.clearBtnLoading = true;
    this.form.patchValue({
      settings_currency_id: 'all',
      name: null,
      status: 'all',
      type: 'all',
      total_amount: null,
      defaultDate: null,
      start_datetime: null,
      end_datetime: null,
      date_type: 'calculation_date'
    });
    this.onSubmit(true);
  }

  private formInit() {
    this.form = new FormGroup({
      settings_currency_id: new FormControl('all'),
      name: new FormControl(null),
      type: new FormControl('all'),
      total_amount: new FormControl(null),
      defaultDate: new FormControl(null), // Do not remove: For Clearing The Range
      start_datetime: new FormControl(null),
      end_datetime: new FormControl(null),
      date_type: new FormControl('calculation_date'),
      status: new FormControl('all'),
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined && key !== 'defaultDate' && formData[key] !== 'all') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_datetime: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_datetime: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  onClearDate() {
    if (this.form.value.start_datetime !== null) {
      this.form.patchValue({ defaultDate: null });
    }
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.searchBtnLoading =  true;
    this.clearBtnLoading = true;
    this.loadingBar.start();

    return this.referralCommissionHistory$ = this.referralCommissionHistoryDataService.getWithQuery(`?page=${page}&perPage=${pageSize}&${this.generateSortingParam()}${params}`).pipe(
      tap(res => {
        this.pagination = this.referralCommissionHistoryDataService.pagination;
        this.loading = false;
        this.clearBtnLoading = false;
        this.searchBtnLoading = false;
        this.dataLength = res.length;
        this.loadingBar.complete();
      })
    );
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }
  
  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }

  onSortColumn(property: string) {
    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }

      }
    }

    // User selection
    this.sortingSelection.sort_by = property;

    if (this.sortingSelection.sort_by === property) {
      // Same column
      this.sortingSelection.sort_order = this.sortingConfig[property];
    } else {
      // Switch to other column
      this.sortingConfig[property] = 'asc';
      this.sortingSelection.sort_order = 'asc';
    }
    // Load Data
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  filterSetting(term: string): ReferralCommissionSetting[] {
    if (this.referralCommissionSettings) {
      return this.referralCommissionSettings.filter(
        setting => setting.name.toLowerCase().includes(term.toLowerCase())
      );
    } else {
      return [];
    }
  }

  searchSetting = (search$: Observable<string>): Observable<ReferralCommissionSetting[]> => {
    return search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(term => term.trim() === '' ? [] : this.filterSetting(term))
    )
  }

  formatSetting(setting: ReferralCommissionSetting): string {
    return `${setting.name} (${setting.type_name})`;
  }
}